import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/features/application/styles/modals.module.css'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { changeReasonSchema, type ChangeReasonValues } from '../types/schemas'
import type { ChangeReasonModalProps } from '../types'

export function ChangeReasonModal({
  isOpen,
  submitting,
  onClose,
  onSubmit,
}: ChangeReasonModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ChangeReasonValues>({
    resolver: zodResolver(changeReasonSchema),
    mode: 'onChange',
    defaultValues: { note: '' },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({ note: '' })
  }, [isOpen, reset])

  const internalSubmit = (values: ChangeReasonValues) => {
    onSubmit(values.note.trim())
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Reason for Change"
      onClose={onClose}
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit(internalSubmit)} className="space-y-4 p-2">
        <div>
          <label htmlFor="change-reason" className={companyFormStyles.formLabel}>
            Reason
          </label>
          <textarea
            id="change-reason"
            rows={4}
            className={cn(companyFormStyles.customFormControl, 'h-auto resize-none')}
            {...register('note')}
          />
          {errors.note && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.note.message}
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-200">
          <button
            type="submit"
            disabled={!isDirty || submitting}
            className={cn(primaryEntityActionButtonClass, 'px-4 py-2')}
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'px-4 py-2')}
          >
            Cancel
          </button>
        </div>
      </form>
    </OnScreenModal>
  )
}
